/**
 * Maps `items` through an async `fn` with at most `limit` calls in flight.
 * Results come back in input order. Rejects on the first rejection, like
 * `Promise.all`; callers that want per-item isolation catch inside `fn`.
 */
export const mapWithConcurrency = async <T, R>(
  items: readonly T[],
  limit: number,
  fn: (item: T, index: number) => Promise<R>,
): Promise<R[]> => {
  if (limit < 1) {
    throw new RangeError('limit must be at least 1');
  }

  const results: R[] = new Array(items.length);
  let nextIndex = 0;

  const worker = async (): Promise<void> => {
    while (nextIndex < items.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fn(items[index], index);
    }
  };

  const workerCount = Math.min(limit, items.length);

  await Promise.all(Array.from({ length: workerCount }, worker));

  return results;
};
